import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import isEmpty from "../../common/helpers/isEmpty";
import { CustomerOrgCompany } from "../../common/types/apiTypes";
import {
  CustomerCompany,
  customerCompanyByOrgId,
  CustomerCompanyFilters,
  CustomerCompanyResult,
  customerOrgAndCompAutocomplete,
  CustomerOrgAndCompAutoCompleteFilters,
  CustomerOrganization,
  CustomerOrgAutoCompleteFilters,
  customerOrgAutocompleteQuery,
  CustomerOrgIdFilter,
  customerOrgNames,
  customerOrgNamesFilters,
  customerOrgNamesResult,
  getCustomerCompanies,
  getCustomerCompanyById,
  getCustomerOrganizations,
  listCustomerOrganizations,
  runCreateCustomerCompany,
  runCreateCustomerOrganization,
} from "./customerAPI";
import {
  CustomerCompanyWithOrganizations,
  CustomerOrganizations,
  CustomerOrganizationsAutocompleteResult,
  CustomerOrganizationsResult,
  RiskLevel,
} from "./types/queries";
import { createCommandAsyncThunk } from "../../app/createApiAsyncThunk";
import {
  CustomerCompanyCreated,
  CustomerOrganizationCreated,
} from "./types/events";
import {
  CreateCustomerCompany,
  CreateCustomerOrganization,
} from "./types/commands";
import { format } from "date-fns";
import { isoFormat } from "../../common/helpers/formatDateTime";

// TODO: edit when the API model is more precisely known
const customerOrganizationsAdapter =
  createEntityAdapter<CustomerOrganizationsResult>({
    sortComparer: (a, b) => a.name.localeCompare(b.name, "fi"),
  });

type SliceState = {
  ids: [];
  entities: {};
  status: "idle" | "pending" | "succeeded" | "failed";
  error?: string;
};

export const fetchCustomerOrganizations = createAsyncThunk<
  Array<CustomerOrganization>,
  CustomerOrgAutoCompleteFilters, //TODO: remove param  as it is not in use
  { state: RootState }
>(
  "customerOrganizations/fetchCustomerOrganizations",
  async (filters: CustomerOrgAutoCompleteFilters, { requestId }) => {
    return await listCustomerOrganizations({
      requestId,
    });
  },
);

export const fetchCustomerOrgAutocomplete = createAsyncThunk<
  Array<CustomerOrganizationsAutocompleteResult>,
  CustomerOrgAutoCompleteFilters,
  { state: RootState }
>(
  "customerOrganizations/fetchCustomerOrganizations",
  async (filters: CustomerOrgAutoCompleteFilters, { requestId }) => {
    return await customerOrgAutocompleteQuery(filters, {
      requestId,
    });
  },
);

export const fetchCustomerCompanyById = createAsyncThunk<
  CustomerCompany,
  CustomerCompanyFilters,
  { state: RootState }
>(
  "customerOrganizations/fetchEezyCompanies",
  async (filters: CustomerCompanyFilters, { requestId }) => {
    const response = await getCustomerCompanyById(filters, {
      requestId,
    });
    return response;
  },
);

export const fetchCustomerCompanyByOrgId = createAsyncThunk<
  CustomerCompanyResult,
  CustomerOrgIdFilter,
  { state: RootState }
>(
  "customerOrganizations/fetchCustomerCompanyByOrgId",
  async (filters: CustomerOrgIdFilter, { requestId }) => {
    const response = await customerCompanyByOrgId(filters, {
      requestId,
    });
    return response;
  },
);

export const fetchCustomerCompanies = createAsyncThunk<
  CustomerCompanyWithOrganizations[],
  {},
  { state: RootState }
>(
  "customerOrganizations/fetchCustomerCompanies",
  async (filters: {}, { requestId }) => {
    const response = await getCustomerCompanies({
      requestId,
    });
    return response;
  },
);

export const fetchCustomerOrgs = createAsyncThunk<
  CustomerOrganizationsResult[],
  CustomerOrganizations,
  { state: RootState }
>(
  "customerOrganizations/fetchCustomerCompanies",
  async (filters: CustomerOrganizations, { requestId, rejectWithValue }) => {
    const response = await getCustomerOrganizations(filters, {
      requestId,
    });

    if (isEmpty(response)) {
      return rejectWithValue("customer_org_not_found");
    }

    return response;
  },
);

export const fetchCustomerOrgNames = createAsyncThunk<
  customerOrgNamesResult[],
  customerOrgNamesFilters,
  { state: RootState }
>(
  "customerOrganizations/fetchCustomerOrgNames",
  async (filters: customerOrgNamesFilters, { requestId }) => {
    const response = await customerOrgNames(filters, { requestId });
    return response;
  },
);

export const searchCustomerOrganizationsAndCompanies = createAsyncThunk<
  CustomerOrgCompany[],
  CustomerOrgAndCompAutoCompleteFilters,
  { state: RootState }
>(
  "customerOrganizations/searchCustomerOrganizationsAndCompanies",
  async (filters: CustomerOrgAndCompAutoCompleteFilters) => {
    const response = await customerOrgAndCompAutocomplete(filters);
    return response;
  },
);

export const createCustomerCompany = createCommandAsyncThunk<
  CreateCustomerCompany,
  CustomerCompanyCreated & CustomerOrganizationCreated
>("customerOrganizations/createCustomerCompany", runCreateCustomerCompany);

export const createCustomerOrganization = createCommandAsyncThunk<
  CreateCustomerOrganization,
  CustomerOrganizationCreated
>(
  "customerOrganizations/createCustomerOrganization",
  runCreateCustomerOrganization,
);

export const customerOrganizations = createSlice({
  name: "customerOrganizations",
  initialState: customerOrganizationsAdapter.getInitialState({
    status: "idle",
  }) as SliceState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchCustomerOrgs.fulfilled, (state, action) => {
      customerOrganizationsAdapter.setAll(state, action.payload);
      state.status = "succeeded";
    });
    builder.addCase(fetchCustomerOrgs.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(fetchCustomerOrgs.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(fetchCustomerOrgNames.fulfilled, (state) => {
      state.status = "succeeded";
    });
    builder.addCase(fetchCustomerOrgNames.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(fetchCustomerOrgNames.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(
      searchCustomerOrganizationsAndCompanies.fulfilled,
      (state) => {
        state.status = "succeeded";
      },
    );
    builder.addCase(
      searchCustomerOrganizationsAndCompanies.pending,
      (state) => {
        state.status = "pending";
      },
    );
    builder.addCase(
      searchCustomerOrganizationsAndCompanies.rejected,
      (state) => {
        state.status = "failed";
      },
    );
    builder.addCase(createCustomerCompany.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(createCustomerCompany.fulfilled, (state, action) => {
      state.status = "succeeded";
      const orgAdded = action.payload.find(
        (event) => event.name === "orgAdd",
      )!!;
      const result: CustomerOrganizationsResult = {
        id: orgAdded.id,
        name: orgAdded.payload.name,
        businessId: orgAdded.payload.businessId,
        customerNumber: orgAdded.payload.customerNumber,
        customerCompanyId: orgAdded.payload.companyId,
        customerCompanyBusinessId: orgAdded.payload.businessId,
        isRepresentative: true,
        salesPermission: {
          verificationDate: format(new Date(), isoFormat),
          riskLevel: RiskLevel.UNKNOWN,
        },
      };
      customerOrganizationsAdapter.addOne(state, result);
    });
    builder.addCase(createCustomerCompany.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(createCustomerOrganization.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(createCustomerOrganization.fulfilled, (state, action) => {
      state.status = "succeeded";
      const orgAdded = action.payload.find(
        (event) => event.name === "orgAdd",
      )!!;
      const result: CustomerOrganizationsResult = {
        id: orgAdded.id,
        name: orgAdded.payload.name,
        businessId: orgAdded.payload.businessId!!,
        customerNumber: orgAdded.payload.customerNumber,
        customerCompanyId: action.meta.arg.companyId,
        customerCompanyBusinessId: "",
        isRepresentative: false,
        salesPermission: {
          verificationDate: format(new Date(), isoFormat),
          riskLevel: RiskLevel.UNKNOWN,
        },
      };
      customerOrganizationsAdapter.addOne(state, result);
    });
    builder.addCase(createCustomerOrganization.rejected, (state) => {
      state.status = "failed";
    });
  },
});

export const {
  selectAll: selectAllCustomerOrganizations,
  selectById: selectCustomerOrganizationById,
} = customerOrganizationsAdapter.getSelectors(
  (state: RootState) => state.customerOrganizations,
);

export const selectCompanyNameById = createSelector(
  [selectAllCustomerOrganizations],
  (orgs) =>
    orgs.reduce<Record<string, string>>((acc, org) => {
      acc[org.customerCompanyId] = org.name;
      return acc;
    }, {}),
);
export const customerOrganizationsReducer = customerOrganizations.reducer;
